"use client";

import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { ArrowLeft } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useSubscriptionCheckout } from "@/lib/payment/useSubscriptionCheckout";
import { effectiveEmailSchema } from "@/lib/validation/email";
import { enterpriseContactPhoneSchema } from "@/lib/validation/enterprisePhone";
import { PlanSelector } from "@/components/subscribe/PlanSelector";
import { PaymentMethodSelector } from "@/components/subscribe/PaymentMethodSelector";
import { BillingIntervalToggle } from "@/components/subscribe/BillingIntervalToggle";
import { SparklineChart } from "@/components/SparklineChart";
import {
  buildSubscriptionPlans,
  type BillingInterval,
  type PlanTier,
} from "@/lib/subscriptionPlans";
import {
  getTierDefinition,
  getTierPriceLabel,
  formatHkd,
} from "@/lib/subscription/planCatalog";
import { track } from "@/components/analytics/track";
import {
  promotionForPlan,
  tierWithOffer,
  type PublicPriceOffer,
} from "@/lib/pricing/offer";

const IS_AIRWALLEX =
  process.env.NEXT_PUBLIC_PAYMENT_PROVIDER === "airwallex";

function isPlanTier(value: string | null): value is PlanTier {
  return value === "starter" || value === "professional" || value === "enterprise";
}

function numberLocaleFor(locale: string): string {
  if (locale === "zh-CN") return "zh-CN";
  if (locale === "zh-HK") return "zh-HK";
  return "en-HK";
}

export function SubscribeCheckout({ offer }: { offer: PublicPriceOffer }) {
  const t = useTranslations("Subscribe");
  const tp = useTranslations("Plans");
  const locale = useLocale();
  const { methods, methodsLoading, initPayment, startCheckout } =
    useSubscriptionCheckout();
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>("starter");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(
    IS_AIRWALLEX ? "card" : null
  );
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [companyNameError, setCompanyNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [canceled, setCanceled] = useState(false);
  const [inquirySuccessOpen, setInquirySuccessOpen] = useState(false);
  const [downloadPlan, setDownloadPlan] = useState<
    "starter" | "professional" | null
  >(null);
  const [downloadInterval, setDownloadInterval] =
    useState<BillingInterval | null>(null);
  const isAlipayPrepaid = IS_AIRWALLEX && selectedMethodId === "alipay";
  const numberLocale = numberLocaleFor(locale);

  const plans = useMemo(
    () => buildSubscriptionPlans(billingInterval, tp, locale, offer),
    [billingInterval, tp, locale, offer]
  );

  const subscriptionSummary = useMemo(() => {
    if (!selectedPlanId || !isPlanTier(selectedPlanId) || selectedPlanId === "enterprise") {
      return null;
    }
    const baseTier = getTierDefinition(selectedPlanId);
    if (!baseTier) return null;
    const tier = tierWithOffer(baseTier, offer);
    const { price, period } = getTierPriceLabel(
      tier,
      billingInterval,
      {
        custom: tp("custom"),
        periodMo: tp("periodMo"),
        periodYr: tp("periodYr"),
        annualDiscount: tp("annualDiscount"),
        yearlySublabel: (monthly, discount) =>
          tp("yearlySublabel", { monthly, discount }),
      },
      numberLocale
    );
    const amountHkd =
      billingInterval === "monthly" ? tier.monthlyAmountHkd! : tier.yearlyAmountHkd!;
    const promo = promotionForPlan(offer, tier.id, billingInterval, locale);
    return {
      planName: tp(`${tier.id}.name`),
      priceDisplay: `${price}${period}`,
      compareAtPrice: promo ? formatHkd(promo.compareAtHkd, numberLocale) : null,
      promotionText: promo?.text ?? null,
      intervalLabel: billingInterval === "monthly" ? tp("monthly") : tp("yearly"),
      billingPhrase: billingInterval === "monthly" ? t("everyMonth") : t("everyYear"),
      amountHkd,
    };
  }, [selectedPlanId, billingInterval, tp, t, numberLocale, offer, locale]);

  const isEnterprise = selectedPlanId === "enterprise";

  const downloadHref = useMemo(() => {
    const params = new URLSearchParams();
    if (downloadPlan) params.set("plan", downloadPlan);
    if (downloadInterval) params.set("interval", downloadInterval);
    const qs = params.toString();
    return qs ? `/product?${qs}` : "/product";
  }, [downloadPlan, downloadInterval]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setCanceled(params.get("canceled") === "1");
    const plan = params.get("plan");
    if (isPlanTier(plan)) {
      setSelectedPlanId(plan);
    }
    const interval = params.get("interval");
    if (interval === "monthly" || interval === "yearly") {
      setBillingInterval(interval);
      setDownloadInterval(interval);
    }
    if (plan === "starter" || plan === "professional") {
      setDownloadPlan(plan);
    }
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
    const method = params.get("method");
    if (method === "alipay" || method === "card") {
      setSelectedMethodId(method);
    }
  }, []);

  useEffect(() => {
    if (!methodsLoading && methods.length > 0 && selectedMethodId === null) {
      const preferred = methods.find((m) => m.recommended) ?? methods[0];
      setSelectedMethodId(preferred.id);
    }
  }, [methods, methodsLoading, selectedMethodId]);

  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError("");
      return;
    }
    const result = effectiveEmailSchema.safeParse(email);
    setEmailError(result.success ? "" : result.error.issues[0]?.message ?? "Invalid email");
  };

  const handleCompanyNameBlur = () => {
    if (!isEnterprise) {
      setCompanyNameError("");
      return;
    }
    const trimmed = companyName.trim();
    if (!trimmed) {
      setCompanyNameError("");
      return;
    }
    if (trimmed.length > 200) {
      setCompanyNameError(t("errorCompanyTooLong"));
      return;
    }
    setCompanyNameError("");
  };

  const handlePhoneBlur = () => {
    if (!isEnterprise) {
      setPhoneError("");
      return;
    }
    if (!phone.trim()) {
      setPhoneError("");
      return;
    }
    const result = enterpriseContactPhoneSchema.safeParse(phone);
    setPhoneError(result.success ? "" : result.error.issues[0]?.message ?? "Invalid phone number");
  };

  const submitLabel = () => {
    if (isEnterprise) return isSubmitting ? t("submitInquiryLoading") : t("submitInquiry");
    if (IS_AIRWALLEX) {
      if (isSubmitting) return t("submitRedirecting");
      return isAlipayPrepaid ? t("submitAlipay") : t("submitCheckout");
    }
    return isSubmitting ? t("submitProcessing") : t("submitPayment");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    const emailResult = effectiveEmailSchema.safeParse(email);
    if (!emailResult.success) {
      setEmailError(emailResult.error.issues[0]?.message ?? "Invalid email");
      return;
    }
    setEmailError("");

    if (!selectedPlanId || !isPlanTier(selectedPlanId)) {
      setSubmitError(t("errorSelectPlan"));
      return;
    }

    let enterpriseInquiryPhone: string | undefined;
    if (isEnterprise) {
      const trimmedCompany = companyName.trim();
      if (!trimmedCompany) {
        setCompanyNameError(t("errorCompanyRequired"));
        return;
      }
      if (trimmedCompany.length > 200) {
        setCompanyNameError(t("errorCompanyTooLong"));
        return;
      }
      setCompanyNameError("");

      const phoneResult = enterpriseContactPhoneSchema.safeParse(phone);
      if (!phoneResult.success) {
        setPhoneError(phoneResult.error.issues[0]?.message ?? "Invalid phone number");
        return;
      }
      setPhoneError("");
      enterpriseInquiryPhone = phoneResult.data;
    }

    if (!isEnterprise) {
      if (!selectedMethodId) {
        setSubmitError(t("errorSelectPayment"));
        return;
      }
    }

    if (!agreeToTerms) {
      setSubmitError(t("errorMustAgree"));
      return;
    }

    if (isEnterprise) {
      setIsSubmitting(true);
      try {
        const res = await fetch("/api/subscribe/enterprise-inquiry", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: emailResult.data,
            companyName: companyName.trim(),
            phone: enterpriseInquiryPhone!,
            locale,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          setSubmitError(data.error ?? t("errorInquiry"));
          return;
        }
        track("contact_submit", {
          meta: { source: "enterprise_inquiry" },
        });
        setInquirySuccessOpen(true);
      } catch {
        setSubmitError(t("errorUnexpected"));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      track("subscribe_start", {
        meta: {
          plan: selectedPlanId,
          interval: billingInterval,
          method: selectedMethodId,
        },
      });
      if (IS_AIRWALLEX) {
        const result = await startCheckout({
          email: emailResult.data,
          tier: selectedPlanId,
          billingInterval,
          paymentMethod: selectedMethodId === "alipay" ? "alipay" : "card",
          locale,
        });

        if (result.success && result.redirectUrl) {
          window.location.href = result.redirectUrl;
          return;
        }

        setSubmitError(result.error ?? t("errorPaymentInit"));
        return;
      }

      const result = await initPayment({
        email: emailResult.data,
        planId: `${selectedPlanId}-${billingInterval}`,
        methodId: selectedMethodId!,
      });

      if (result.success && result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }

      setSubmitError(result.error ?? t("errorPaymentInit"));
    } catch {
      setSubmitError(t("errorUnexpected"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkClass =
    "text-teal-light hover:text-teal-400 underline";

  return (
    <>
      <Header />
      <div className="min-h-screen quant-grid pt-[calc(73px+var(--announcement-bar-height,0px))]">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-100 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("backHome")}
            </Link>

            <div className="relative overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900/35 mb-10 min-h-[220px]">
              <div className="absolute inset-0 opacity-[0.35] pointer-events-none">
                <SparklineChart />
              </div>
              <div className="relative z-10 p-8 md:p-10">
                <p className="text-teal-light text-[11px] font-mono tracking-[0.2em] uppercase mb-3">
                  {t("eyebrow")}
                </p>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3 tracking-tight">
                  {t("title")}
                </h1>
                <p className="text-slate-400 max-w-2xl text-sm md:text-base leading-relaxed">
                  {t("intro")}
                </p>
              </div>
            </div>

            {canceled && (
              <div className="mb-8 p-4 rounded-lg bg-slate-800/50 border border-slate-600 text-slate-300 text-sm">
                {t("canceledBanner")}
              </div>
            )}

            <div className="mb-8 flex flex-col gap-4 rounded-xl border border-slate-700 bg-slate-800/40 p-5 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-100 mb-1">
                  {t("downloadFirstTitle")}
                </p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {t("downloadFirstBody")}
                </p>
              </div>
              <Link
                href={downloadHref}
                onClick={() =>
                  track("cta_click", { meta: { label: "subscribe_to_download" } })
                }
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-teal-primary/50 px-4 py-2.5 text-sm font-semibold text-teal-light hover:border-teal-light transition-colors"
              >
                {t("downloadFirstButton")}
              </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-10">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  {t("emailLabel")}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  placeholder={t("emailPlaceholder")}
                  className={`w-full px-4 py-3 rounded-lg bg-slate-800/50 border text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-primary ${
                    emailError ? "border-red-500/50" : "border-slate-700"
                  }`}
                  autoComplete="email"
                />
                {emailError && (
                  <p className="mt-2 text-sm text-red-400">{emailError}</p>
                )}
              </div>

              {isEnterprise && (
                <>
                  <div>
                    <label
                      htmlFor="company-name"
                      className="block text-sm font-medium text-slate-300 mb-2"
                    >
                      {t("companyLabel")}
                    </label>
                    <input
                      id="company-name"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      onBlur={handleCompanyNameBlur}
                      placeholder={t("companyPlaceholder")}
                      className={`w-full px-4 py-3 rounded-lg bg-slate-800/50 border text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-primary ${
                        companyNameError ? "border-red-500/50" : "border-slate-700"
                      }`}
                      autoComplete="organization"
                    />
                    {companyNameError && (
                      <p className="mt-2 text-sm text-red-400">{companyNameError}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="contact-phone"
                      className="block text-sm font-medium text-slate-300 mb-2"
                    >
                      {t("phoneLabel")}
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onBlur={handlePhoneBlur}
                      placeholder={t("phonePlaceholder")}
                      className={`w-full px-4 py-3 rounded-lg bg-slate-800/50 border text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-primary ${
                        phoneError ? "border-red-500/50" : "border-slate-700"
                      }`}
                    />
                    {phoneError && (
                      <p className="mt-2 text-sm text-red-400">{phoneError}</p>
                    )}
                  </div>
                </>
              )}

              {!isEnterprise && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <label className="block text-sm font-medium text-slate-300">
                      {t("billingLabel")}
                    </label>
                    <BillingIntervalToggle
                      value={billingInterval}
                      onChange={setBillingInterval}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-4">
                  {t("planLabel")}
                </label>
                <PlanSelector
                  plans={plans}
                  selectedPlanId={selectedPlanId}
                  onSelect={setSelectedPlanId}
                />
              </div>

              {!isEnterprise && subscriptionSummary && (
                <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-6 space-y-3">
                  <h2 className="text-sm font-semibold text-slate-200 uppercase tracking-wide">
                    {t("summaryTitle")}
                  </h2>
                  <dl className="grid gap-2 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-400">{t("summaryPlan")}</dt>
                      <dd className="text-slate-100 font-medium text-right">
                        {subscriptionSummary.planName}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-400">{t("summaryPrice")}</dt>
                      <dd className="text-right">
                        {subscriptionSummary.compareAtPrice ? (
                          <span className="mr-2 text-slate-500 line-through">
                            {subscriptionSummary.compareAtPrice}
                          </span>
                        ) : null}
                        <span className="font-medium text-slate-100">
                          {subscriptionSummary.priceDisplay}
                        </span>
                        {subscriptionSummary.promotionText ? (
                          <span className="ml-2 inline-block rounded-full bg-maroon px-2 py-0.5 text-xs font-semibold text-white">
                            {subscriptionSummary.promotionText}
                          </span>
                        ) : null}
                        <span className="text-slate-400">
                          {" "}
                          ({subscriptionSummary.intervalLabel.toLowerCase()})
                        </span>
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-400">{t("summaryBilling")}</dt>
                      <dd className="text-slate-300 text-right">
                        {isAlipayPrepaid
                          ? t("prepaidNoAutoRenew")
                          : t("cardAutoRenew")}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-400">
                        {isAlipayPrepaid ? t("summaryRenewal") : t("summaryCancellation")}
                      </dt>
                      <dd className="text-slate-300 text-right">
                        {isAlipayPrepaid
                          ? t("renewBeforeExpiry")
                          : t("cancelAtPeriodEnd")}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-400">{t("summaryDelivery")}</dt>
                      <dd className="text-slate-300 text-right">
                        {t("deliveryLicense")}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-slate-500 text-xs pt-1 border-t border-slate-700/80">
                    {isAlipayPrepaid
                      ? t("paymentMethodsHintAlipay")
                      : t("paymentMethodsHintCard")}
                  </p>
                </div>
              )}

              {!isEnterprise && IS_AIRWALLEX && (
                <div className="space-y-4">
                  <PaymentMethodSelector
                    methods={methods}
                    selectedId={selectedMethodId}
                    onSelect={setSelectedMethodId}
                    loading={methodsLoading}
                    hint={t("paymentMethodHint")}
                  />
                  <p className="text-slate-500 text-sm">
                    {isAlipayPrepaid ? t("authAlipay") : t("authCard")}
                  </p>
                </div>
              )}

              {!isEnterprise && !IS_AIRWALLEX && (
                <div>
                  <PaymentMethodSelector
                    methods={methods}
                    selectedId={selectedMethodId}
                    onSelect={setSelectedMethodId}
                    loading={methodsLoading}
                  />
                </div>
              )}

              <div className="flex gap-3">
                <input
                  id="agree-terms"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-teal-primary focus:ring-teal-primary"
                />
                <label
                  htmlFor="agree-terms"
                  className="text-sm text-slate-300 cursor-pointer"
                >
                  {t.rich("consent", {
                    terms: (chunks) => (
                      <Link
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClass}
                      >
                        {chunks}
                      </Link>
                    ),
                    privacy: (chunks) => (
                      <Link
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClass}
                      >
                        {chunks}
                      </Link>
                    ),
                    refund: (chunks) => (
                      <Link
                        href="/refund-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClass}
                      >
                        {chunks}
                      </Link>
                    ),
                  })}
                </label>
              </div>

              {!isEnterprise && subscriptionSummary && (
                <p className="text-slate-400 text-sm leading-relaxed">
                  {isAlipayPrepaid
                    ? t("chargeNoticeAlipay", {
                        amount: formatHkd(
                          subscriptionSummary.amountHkd,
                          numberLocale
                        ),
                      })
                    : t("chargeNoticeCard", {
                        amount: formatHkd(
                          subscriptionSummary.amountHkd,
                          numberLocale
                        ),
                        interval: subscriptionSummary.billingPhrase,
                      })}
                </p>
              )}

              {submitError && <p className="text-sm text-red-400">{submitError}</p>}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-6 rounded-lg bg-maroon hover:bg-maroon-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors"
              >
                {submitLabel()}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
      <Footer />

      {inquirySuccessOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full rounded-xl border border-slate-600 bg-slate-900 p-8 shadow-xl"
          >
            <h2 className="text-xl font-semibold text-slate-100 mb-3">
              {t("modalTitle")}
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              {t("modalBody")}
            </p>
            <button
              type="button"
              onClick={() => setInquirySuccessOpen(false)}
              className="w-full py-3 px-4 rounded-lg bg-teal-primary hover:bg-teal-primary/90 text-slate-950 font-semibold"
            >
              {t("modalOk")}
            </button>
          </motion.div>
        </div>
      )}
    </>
  );
}
